import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useCrossTabAuthSync } from "@/hooks/useCrossTabAuthSync";
import { useIdleSignOut } from "@/hooks/useIdleSignOut";
import { mfaRedirectPath } from "@/lib/auth/jwt";
import {
  enrollPending,
  recoveryCodesPending,
} from "@/lib/auth/pendingRecoveryCodes";

/** Where a pending organisation waits, and the one path this guard must not
 *  bounce away from — it is the destination of its own redirect. */
const PENDING_APPROVAL_PATH = "/pending-approval";

/** The same, for an organisation whose registration was refused. */
const REJECTED_PATH = "/registration-rejected";

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const {
    isAuthenticated,
    isTokenExpired,
    accessToken,
    mspStatus,
    companyStatus,
    logout,
  } = useAuthStore();
  const location = useLocation();

  // Every authenticated route passes through here, including the three that do
  // not use MainLayout — pending-approval, recovery codes, MFA disable. A tab
  // parked on the recovery-codes screen has secrets already painted, so it is
  // the last one that should miss a sign-out happening elsewhere.
  useCrossTabAuthSync();

  // Here for the same reason: every signed-in page, MainLayout or not, ends
  // after the idle timeout. Declared before the expiry effect below so a tab
  // reopened long after its last use says it was signed out for inactivity.
  useIdleSignOut();

  // Being signed out here has to mean signed out on the server too. This path
  // used to redirect and nothing else: the access token expired after an hour,
  // the reader was returned to the login screen, and the refresh token behind
  // them stayed valid for the rest of its seven days — still usable, still
  // listed under Active Sessions, belonging to a browser that had already been
  // signed out.
  //
  // `logout` revokes with the *refresh* token, which is still valid here; the
  // access token is expired by definition on this path. That only works because
  // `/auth/logout/` now takes the refresh token as its proof — while it
  // required a live access token, this call was refused before the view ran and
  // revoked nothing, which is the failure this whole branch is about.
  const sessionEnded = isAuthenticated && isTokenExpired();
  useEffect(() => {
    if (sessionEnded) {
      logout();
    }
  }, [sessionEnded, logout]);

  if (!isAuthenticated || isTokenExpired()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const mfaTarget = accessToken ? mfaRedirectPath(accessToken) : null;
  if (mfaTarget) {
    return <Navigate to={mfaTarget} replace />;
  }

  // A refused organisation is turned away before anything else about its state
  // is considered: the server already refuses every request it makes, so
  // sending it to the pending screen — or worse, letting a page start
  // fetching — would only produce a wall of failures with no explanation.
  if (mspStatus === "rejected" || companyStatus === "rejected") {
    if (location.pathname !== REJECTED_PATH) {
      return <Navigate to={REJECTED_PATH} replace />;
    }
    return <>{children}</>;
  }

  // The pending screen is itself behind this guard, so redirecting to it from
  // it is a loop: guard renders <Navigate> to the path it is already on, the
  // router re-renders, the guard runs again. React gives up with "Maximum
  // update depth exceeded", unmounts the tree, and the reader is left looking
  // at a blank page with no way out — which is what a pending MSP saw the
  // moment registration and MFA setup sent them here.
  if (
    (mspStatus === "pending_approval" ||
      companyStatus === "pending_approval") &&
    location.pathname !== PENDING_APPROVAL_PATH
  ) {
    return <Navigate to={PENDING_APPROVAL_PATH} replace />;
  }

  // A session that just enrolled has not seen its recovery codes yet. Checked
  // here because this is the first point at which the session has both passed
  // MFA (the codes endpoint requires it) and reached a real page — wherever
  // that page happens to be, and whichever guard sent it there.
  //
  // Pending organisations are excluded rather than relying on the branch above
  // returning first. On this branch it does; on TW-45759711 the pending branch
  // gains its own path exemption, so a pending reader already sitting on
  // /pending-approval falls through to here and gets pushed to a screen with no
  // route back. Stated as a condition so it holds whichever order the two
  // merge in, instead of depending on one of them not changing.
  const codesPath = "/auth/mfa/recovery-codes";
  const organisationPending =
    mspStatus === "pending_approval" || companyStatus === "pending_approval";
  if (
    recoveryCodesPending() &&
    !organisationPending &&
    location.pathname !== codesPath
  ) {
    return <Navigate to={codesPath} replace />;
  }

  if (enrollPending() && !organisationPending) {
    return <Navigate to="/auth/mfa/enroll" replace />;
  }

  return <>{children}</>;
}
